/**
 * V4 REPUTATION (Agent Brains 5.6, "rating, velocity and presence"): the
 * weekly synthesis of what posted reviews are about.
 *
 * A CLOSED lexicon, on purpose (CLAUDE.md section 0, precision beats recall):
 * a review is themed only when its words match a theme's patterns below. A
 * review that matches nothing is counted as unthemed and no model is asked to
 * guess a theme for it. Matching is on whole words, case-insensitive, over the
 * review body, which is a reviewer's own public text and is read here and
 * never copied anywhere else (the synthesis event carries counts and theme
 * codes only, section 10).
 *
 * Adding a theme or a pattern is a code change reviewed like any other; no
 * agent writes this file.
 */

export const REVIEW_THEMES = [
  "sizing",
  "turnaround",
  "service",
  "craftsmanship",
  "communication",
  "value_perception",
] as const;
export type ReviewTheme = (typeof REVIEW_THEMES)[number];

/**
 * The fewest reviews on one theme before Campaign Op is handed a content
 * suggestion for it. AWAITING RATIFICATION (design V4: n >= 3).
 */
export const REVIEW_THEME_MIN_N = 3;

/**
 * G8 (TA-819, OPEN row 191): the lowest star a review may carry and still count
 * a theme toward Campaign Op's content suggestions. The vault's Reputation node
 * "detects what people like" and Campaign suggests "content based on what
 * people like", so a 1 to 3 star complaint theme is reported in the synthesis
 * and never becomes a content suggestion.
 */
// AWAITING RATIFICATION
export const REVIEW_CONTENT_MIN_RATING = 4;

/**
 * G8: themes that never become a content suggestion, whatever the star. A
 * value_perception wave is a price-led wave, and no automated message leads
 * with money (CLAUDE.md section 0.5). The synthesis still reports the theme.
 */
export const REVIEW_CONTENT_EXCLUDED_THEMES: readonly ReviewTheme[] = ["value_perception"];

const LEXICON: Readonly<Record<ReviewTheme, readonly RegExp[]>> = {
  sizing: [/\bsize[ds]?\b/, /\bsizing\b/, /\bresiz(?:e|ed|ing)\b/, /\bfit(?:s|ted|ting)?\b/, /\btoo (?:tight|loose|small|big)\b/],
  turnaround: [
    /\blate\b/,
    /\bdelay(?:s|ed)?\b/,
    /\bturnaround\b/,
    /\bslow(?:er|ly)?\b/,
    /\bweeks?\b/,
    /\bon time\b/,
    /\bpromised\b/,
    /\bwaited?\b/,
    /\bquick(?:ly)?\b/,
    /\bfast\b/,
  ],
  service: [
    /\bservice\b/,
    /\bhelpful\b/,
    /\bfriendly\b/,
    /\brude\b/,
    /\bstaff\b/,
    /\bteam\b/,
    /\bwelcom(?:e|ed|ing)\b/,
    /\battentive\b/,
    /\bprofessional\b/,
  ],
  craftsmanship: [
    /\bcraft(?:s|ed|smanship|smen)?\b/,
    /\bquality\b/,
    /\bwell[- ]made\b/,
    /\bstones?\b/,
    /\bsetting\b/,
    /\bpolish(?:ed)?\b/,
    /\bdetail(?:s|ed)?\b/,
    /\bflawless\b/,
    /\bbroke(?:n)?\b/,
    /\bscratch(?:ed|es)?\b/,
  ],
  communication: [
    /\bcommunicat(?:e|ed|es|ion)\b/,
    /\breplied\b/,
    /\brespond(?:ed|s)?\b/,
    /\bresponse\b/,
    /\bupdates?\b/,
    /\bupdated\b/,
    /\bemails?\b/,
    /\bcalled back\b/,
    /\bignored\b/,
    /\bkept me (?:posted|informed)\b/,
  ],
  value_perception: [
    /\bovercharg(?:e|ed)\b/,
    /\boverpriced\b/,
    /\bpric(?:e|es|ed|ey)\b/,
    /\bworth\b/,
    /\bexpensive\b/,
    /\bcost(?:s|ly)?\b/,
    /\bvalue\b/,
    /\bmoney\b/,
  ],
};

/** Themes this review text carries, in lexicon order. Empty: unthemed. */
export function themesOf(body: string | null | undefined): ReviewTheme[] {
  if (body === null || body === undefined) return [];
  const text = body.toLowerCase();
  if (text.trim() === "") return [];
  return REVIEW_THEMES.filter((theme) => LEXICON[theme].some((re) => re.test(text)));
}

export interface ThemeCount {
  readonly theme: ReviewTheme;
  readonly n: number;
  readonly reviewIds: readonly string[];
}

/** Reviews per star, keyed "1" to "5". Every review counts once, themed or not. */
export type StarCounts = Readonly<Record<"1" | "2" | "3" | "4" | "5", number>>;

export interface ReviewSynthesis {
  readonly n: number;
  readonly unthemed: number;
  /** Themes with at least one review, most reviews first, ties in lexicon order. Every star counts here. */
  readonly themes: readonly ThemeCount[];
  /**
   * The content half (G8): themes counted from 4 and 5 star reviews only, value_perception never, same order
   * rule. Campaign Op is handed only what people like.
   */
  readonly contentThemes: readonly ThemeCount[];
  /** G25: a deterministic per-star count over the same reviews. */
  readonly stars: StarCounts;
}

const STAR_KEYS = ["1", "2", "3", "4", "5"] as const;

function rank(ids: ReadonlyMap<ReviewTheme, readonly string[]>): ThemeCount[] {
  const out: ThemeCount[] = REVIEW_THEMES.filter((t) => ids.has(t)).map((t) => ({
    theme: t,
    n: (ids.get(t) ?? []).length,
    reviewIds: ids.get(t) ?? [],
  }));
  out.sort((a, b) => b.n - a.n);
  return out;
}

export function synthesizeReviews(
  reviews: readonly { readonly id: string; readonly rating: number; readonly body: string | null }[],
): ReviewSynthesis {
  const ids = new Map<ReviewTheme, string[]>();
  const contentIds = new Map<ReviewTheme, string[]>();
  const stars: Record<(typeof STAR_KEYS)[number], number> = { "1": 0, "2": 0, "3": 0, "4": 0, "5": 0 };
  let unthemed = 0;
  for (const r of reviews) {
    // Loud, never a guessed star: the table's CHECK holds 1 to 5, so anything else is a broken read.
    if (!Number.isInteger(r.rating) || r.rating < 1 || r.rating > 5) {
      throw new RangeError(`synthesizeReviews: review ${r.id} has rating ${String(r.rating)}, expected an integer 1 to 5`);
    }
    stars[STAR_KEYS[r.rating - 1] as (typeof STAR_KEYS)[number]] += 1;
    const themes = themesOf(r.body);
    if (themes.length === 0) {
      unthemed += 1;
      continue;
    }
    for (const t of themes) {
      const list = ids.get(t) ?? [];
      list.push(r.id);
      ids.set(t, list);
      if (r.rating >= REVIEW_CONTENT_MIN_RATING && !REVIEW_CONTENT_EXCLUDED_THEMES.includes(t)) {
        const c = contentIds.get(t) ?? [];
        c.push(r.id);
        contentIds.set(t, c);
      }
    }
  }
  return { n: reviews.length, unthemed, themes: rank(ids), contentThemes: rank(contentIds), stars };
}

/**
 * The themes with enough reviews to hand Campaign Op a content suggestion: the content half only, so a
 * complaint theme or value_perception never reaches it (G8).
 */
export function contentSuggestionThemes(s: ReviewSynthesis): ThemeCount[] {
  return s.contentThemes.filter((t) => t.n >= REVIEW_THEME_MIN_N);
}
